<template>
  <div class="print-area">
    <div class="toolbar">
      <div class="toolbar-field">
        <label>Start date</label>
        <datepicker input-class="bg-dark text-white" :disabled-dates="{from: new Date()}" bootstrap-styling  v-model="fromDate" class="text-dark"></datepicker>
      </div>
      <div class="toolbar-field">
        <label>End date</label>
        <datepicker input-class="bg-dark text-white" :disabled-dates="{from: new Date()}" bootstrap-styling  v-model="toDate" class="text-dark"></datepicker>
      </div>
      <div class="d-print-none d-flex" style="gap: 0.5rem">
        <button @click="fetchInvoices" :class="!fromDate || !toDate ? 'btn-secondary disabled' : 'btn-primary'" class="btn px-4">
          <b-icon-search class="mr-2"></b-icon-search>Search
        </button>
        <b-button @click="printaj" variant="secondary" class="d-none d-md-inline-block"><b-icon-printer class="mr-1"></b-icon-printer>Print</b-button>
      </div>
    </div>

    <div class="w-100 position-relative" v-if="loading">
      <div class="w-100 mt-5 position-absolute text-center">
        <b-icon class="text-dark" icon="arrow-clockwise" animation="spin" font-scale="3"></b-icon>
      </div>
    </div>

    <div v-else-if="invoices.length" class="row mx-0">
      <div class="w-100">
        <table class="table">
          <thead>
          <tr>
            <th scope="col">Invoiced On</th>
            <th class="d-none d-sm-table-cell text-center" scope="col">Invoice Number</th>
            <th class="d-none d-sm-table-cell text-center" scope="col">Sum Kr.</th>
            <th class="d-none d-sm-table-cell text-center" scope="col">Status</th>
            <th class="d-none d-sm-table-cell d-print-none text-center" scope="col">Excel</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, i) in invoices" v-bind:key="i">
            <th @click="renderInvoice(row)" scope="row" class="pointer">{{moment(row.createdAt).format('DD.MM.yyyy HH:mm')}}</th>
            <td class="d-none d-sm-table-cell text-center">{{row.number}}</td>
            <td class="d-none d-sm-table-cell text-center">{{row.items.reduce((value, item) => value += item.qtt * item.price, 0).toFixed(2)}}</td>
            <td v-if="row.paid" class="d-none d-sm-table-cell text-center text-success"><b-badge variant="success">Paid POS</b-badge></td>
            <td v-else class="d-none d-sm-table-cell text-center"><b-badge variant="secondary">Tripletex</b-badge></td>
            <td class="d-none d-sm-table-cell d-print-none text-center"><b-button @click="getExcelFile(row)" variant="outline-secondary" size="sm"><b-icon-download class="mr-1"></b-icon-download>File</b-button></td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="stat-grid">
        <div class="stat-tile success">
          <p class="stat-label">Total paid locally</p>
          <p class="stat-value">Kr. {{totals.paid.toFixed(2).toLocaleString('en')}}</p>
        </div>
        <div class="stat-tile accent">
          <p class="stat-label">Total in Tripletex</p>
          <p class="stat-value">Kr. {{totals.tripleTex.toFixed(2).toLocaleString('en')}}</p>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';
import axios from 'axios';

import fixed from '../../fixed';

export default {
  components: {
    Datepicker,
  },
  data: () => ({
    loading: false,
    fromDate: null,
    toDate: null,
    invoices: [],
    totals: {
      tripleTex: 0,
      paid: 0,
    },

    moment,
  }),
  methods: {
    async fetchInvoices() {
      // check if we have both dates
      if (!this.fromDate || !this.toDate) return;
      // reset totals
      this.loading = true;
      this.totals = {
        tripleTex: 0,
        paid: 0,
      };

      // call the API
      try {
        const { data } = await axios.get(
          `${fixed.API_URL}/admin/invoice-reports?from=${moment(this.fromDate).format('yyyy-MM-DD')}&to=${moment(this.toDate).format('yyyy-MM-DD')}`,
          { withCredentials: true },
        );

        if (data && data.length) {
          // list of invoices to show on the page
          this.invoices = data;
          // calculated totals
          this.totals = data.reduce((acc, invoice) => {
            if (invoice.items.length) {
              invoice.items.forEach((item) => {
                if (invoice.paid) {
                  acc.paid += (item.qtt * item.price);
                } else {
                  acc.tripleTex += (item.qtt * item.price);
                }
              });
            }
            return acc;
          }, this.totals);
        } else {
          this.$toast.info('No data for selected range');
          this.invoices = [];
        }
      } catch (error) {
        if (error?.response && error.response?.status === 403) {
          this.$toast.warning('you\'re not authorised for this action ✋🏻');
        } else {
          this.$toast.error(error?.response?.data ? error.response.data : 'No data for selected params');
        }
      }
      this.loading = false;
    },
    async renderInvoice(invoice) {
      this.loading = true;

      try {
        const { data } = await axios.post(`${fixed.API_URL}/admin/otf-invoice`, invoice, { withCredentials: true, responseType: 'arraybuffer' });
        const blob = new Blob([data], { type: 'application/pdf' });
        const blobURL = URL.createObjectURL(blob);
        window.open(blobURL);
      } catch (err) {
        this.$toast.error('can\'t render invoice');
      }
      this.loading = false;
    },
    async getExcelFile(invoice) {
      this.loading = true;

      try {
        const response = await axios.post(`${fixed.API_URL}/admin/otf-excel`, invoice, { withCredentials: true, responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = invoice?.client?.name || 'price_analysis.xlsx';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
      } catch (error) {
        this.$toast.error('can\'t download price analysis file');
      }

      this.loading = false;
    },
    printaj() {
      if (!this.invoices.length) return;
      window.print();
    },
  },
};

</script>

<style>

</style>
